import axios, {AxiosInstance, AxiosResponse} from 'axios';
import { ApiResponse } from '../types/api/ApiResponse';
import DateUtils from '../utils/DateUtils';
import {IPostRequest} from '../types/api/PostRequest';

type TokenResponse = {
    access_token: string;
    expires_in: number;
}

export class RestClient {
    axios: AxiosInstance;
    tokenUrl: string;
    apiUrl: string;
    debugMode: boolean;
    useRecaptcha: boolean;
    recaptchaUrl: string;
    token: string|null;
    tokenExpiresAt: number;
    companyInfoId?: string|null;

    constructor(
        tokenUrl: string,
        apiUrl: string,
        debugMode: boolean,
        useRecaptcha: boolean,
        recaptchaUrl: string,
        companyInfoId?: string | null,
    ) {
        this.axios = axios.create({
            transformResponse: [
                this.transformDates,
            ],
        });
        this.axios.defaults.headers.common.Accept = 'application/json';
        this.tokenUrl = tokenUrl;
        this.apiUrl = apiUrl;
        this.debugMode = debugMode;
        this.companyInfoId = companyInfoId;
        this.useRecaptcha = useRecaptcha;
        this.recaptchaUrl = recaptchaUrl;
        this.token = null;
    }

    public transformDates = (data: any): any => {
        if (typeof data === 'string') {
            try {
                return JSON.parse(data, DateUtils.parseDate);
            } catch (error) {
                // If error occurs, return original data.
                return data;
            }
        }

        return data;
    }

    private getHttpHeaders = async (): Promise<any> => {
        const now = new Date().getTime();
        if (!this.token || !this.tokenExpiresAt || this.tokenExpiresAt <= now) {
            await this.getAccessToken();
        }

        return {
            Authorization: 'Bearer ' + this.token,
            'Content-Type': 'application/json',
        };
    }

    private getAccessToken = async () => {
        try {
            const response: AxiosResponse<TokenResponse> = await this.axios.get(
                this.tokenUrl,
            );

            this.token = response.data.access_token;
            this.tokenExpiresAt = new Date().getTime() + response.data.expires_in * 1000;

            return { success: true, token: this.token };
        }
        catch (error: any) {
            return { success: false };
        }
    }

    get = async (path: string, params: any = {}): Promise<ApiResponse> => {
        this.debugMode && console.log(`get ${path}`, params);

        try {
            const headers = await this.getHttpHeaders();

            const requestParams: any = {
                ...params,
            }

            // Add default company info id, if not already set.
            if (this.companyInfoId) {
                requestParams.companyInfoId ??= this.companyInfoId;
            }

            const config = {
                headers: headers,
                params: requestParams,
            };

            const response: AxiosResponse = await this.axios.get(
                `${this.apiUrl}${path}`,
                config
            );

            this.debugMode && console.log(response);

            return { success: true, response: response, ...response };
        } catch (error: any) {
            this.debugMode && console.log(error);
            return { success: false, ...error };
        }
    };

    post = async (path: string, body: IPostRequest): Promise<ApiResponse> => {
        this.debugMode && console.log(`post ${path}`, body);

        try {
            const headers = await this.getHttpHeaders();

            // Add default company info id, if not already set.
            if (this.companyInfoId) {
                body.companyInfoId ??= this.companyInfoId;
            }

            if (this.useRecaptcha) {
                if (!body.recaptchaToken) {
                    throw new Error('Recaptcha missing');
                }

                const recaptchaResponse: AxiosResponse<TokenResponse> = await this.axios.post(
                    this.recaptchaUrl,
                    {
                        recaptchaToken: body.recaptchaToken,
                    },
                );

                if (recaptchaResponse.status !== 200) {
                    throw new Error('Recaptcha invalid');
                }
            }

            const response: AxiosResponse = await this.axios.post(
                `${this.apiUrl}${path}`,
                body,
                { headers }
            );

            this.debugMode && console.log(response);

            return { success: true, response: response, ...response };
        } catch (error: any) {
            this.debugMode && console.log(error);
            return { success: false, ...error.response };
        }
    };

}
